import {
  useDeleteFactorsConditionMutation,
  useGetFactorsConditionsQuery,
} from '@/app/api';
import { GetFactorsConditionsResponse } from '@/app/api/types';
import { Box, Button, Grid, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import TrashIcon from '../../../app/assets/icons/trash.svg?react';
import { FactoringConditionsModal } from './FactoringConditionsModal';
import { useRef, useState } from 'react';
import { BaseModalRef } from '@/app/helpers/types';
import { useNotification } from '@/shared/contexts/NotificationContext';
import { getFetchEror } from '@/app/helpers';
import ConfirmDeletion from '@/shared/confirm-deletion';
import { EmptyDataPlaceholder } from '@/shared/ui/empty-data-placeholder';
import DocumentIcon from '@/app/assets/icons/file-xmark.svg?react';
import ComponentWithLoader from '@/shared/ComponentWithLoader/ComponentWithLoader';

const ConditionsComponent = () => {
  const { t } = useTranslation();
  const { showNotification } = useNotification();

  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [editingConditionId, setEditingConditionId] = useState<number | null>(
    null
  );

  const factoringConditionsModalRef = useRef<BaseModalRef>(null);

  const {
    data: factorsConditions,
    isLoading,
    refetch,
  } = useGetFactorsConditionsQuery();
  const [deleteFactorsCondition] = useDeleteFactorsConditionMutation();

  const getFactoringTypeText = (
    isForDisclosedFactoring: boolean | null,
    isForUndisclosedFactoring: boolean | null,
    isForReverseFactoring: boolean | null,
    isForExportFactoring: boolean | null,
    isForImportFactoring: boolean | null,
    t: (key: string) => string
  ): string => {
    const types: string[] = [];

    if (isForDisclosedFactoring) {
      types.push(t('settings.factoring_types.disclosed'));
    }

    if (isForUndisclosedFactoring) {
      types.push(t('settings.factoring_types.undisclosed'));
    }

    if (isForReverseFactoring) {
      types.push(t('settings.factoring_types.reverse'));
    }

    if (isForExportFactoring) {
      types.push(t('settings.factoring_types.export'));
    }

    if (isForImportFactoring) {
      types.push(t('settings.factoring_types.import'));
    }

    if (types.length === 0) {
      return t('settings.factoring_types.no_selected_types');
    }

    return types.join(', ');
  };

  const handleAddCondition = () => {
    factoringConditionsModalRef.current?.open();
  };

  const handleDeleteCondition = async (id: number) => {
    try {
      await deleteFactorsCondition({ id }).unwrap();
      showNotification(t('settings.condition_successfully_deleted'), 'success');
    } catch (error) {
      showNotification(getFetchEror(error), 'error');
    } finally {
      refetch();
    }
  };

  const askDeleteCondition = (id: number) => {
    setDeletingId(id);
    setConfirmOpen(true);
  };

  const closeConfirm = () => {
    setConfirmOpen(false);
    setDeletingId(null);
  };

  const confirmDelete = async () => {
    if (deletingId == null) return;
    await handleDeleteCondition(deletingId);
    closeConfirm();
  };

  const handleEditCondition = (conditionId: number) => {
    setEditingConditionId(conditionId);
    factoringConditionsModalRef.current?.open();
  };

  const handleModalSuccess = () => {
    setEditingConditionId(null);
    refetch();
  };

  return (
    <ComponentWithLoader isLoading={isLoading}>
      <Stack
        sx={{
          background: 'rgba(21, 85, 252, 0.04)',
          p: 2.4,
          borderRadius: '1.2rem',
        }}
      >
        <Stack direction='column'>
          <Stack direction='row' justifyContent='space-between'>
            <Typography fontSize='1.7rem' fontWeight={600}>
              {t('settings.conditions')}
            </Typography>
            {factorsConditions?.data?.length !== 0 && (
              <Button
                sx={{
                  background: '#fff',
                  color: '#0B1370',
                  '&:hover': {
                    background: '#E6F1FF',
                  },
                }}
                onClick={handleAddCondition}
              >
                {t('settings.add_condition')}
              </Button>
            )}
          </Stack>

          {factorsConditions?.data?.length === 0 ? (
            <EmptyDataPlaceholder
              title={t('settings.no_conditions_title')}
              subtitle={t('settings.no_conditions_subtitle')}
              isEmpty={!isLoading && !factorsConditions?.data?.length}
              isLoading={isLoading}
              icon={<DocumentIcon />}
              endAdornment={
                <Button
                  sx={{ fontSize: '1.4rem', fontWeight: 600 }}
                  onClick={handleAddCondition}
                >
                  {t('settings.add_conditions')}
                </Button>
              }
            />
          ) : (
            [...(factorsConditions?.data ?? [])]
              .sort((a, b) => a.termInDays - b.termInDays)
              .map((condition: GetFactorsConditionsResponse) => (
                <Stack key={condition.id} spacing={2} mt={2}>
                  <Box
                    sx={{
                      background: '#0D1D580A',
                      borderRadius: '1.2rem',
                      mt: 2,
                    }}
                  >
                    <Stack
                      direction='row'
                      justifyContent='space-between'
                      alignItems='center'
                      sx={{ padding: 1.2 }}
                    >
                      <Typography fontSize='1.7rem' fontWeight={600}>
                        {condition.title}
                      </Typography>
                      <Stack direction='row' spacing={2}>
                        <Button
                          sx={{
                            background: '#fff',
                            color: '#0B1370',
                            '&:hover': {
                              background: '#E6F1FF',
                            },
                          }}
                          onClick={() => handleEditCondition(condition.id)}
                        >
                          {t('settings.edit')}
                        </Button>
                        <Button
                          sx={{
                            minWidth: '4rem',
                            height: '4rem',
                            p: 0,
                            background: '#fff',
                            borderRadius: '0.8rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              background: '#E6F1FF',
                            },
                            '& svg': {
                              width: '1.6rem',
                              height: '1.6rem',
                              '& path': {
                                stroke: 'rgba(13, 29, 88, 0.5)',
                              },
                            },
                          }}
                          onClick={() => askDeleteCondition(condition.id)}
                        >
                          <TrashIcon />
                        </Button>
                      </Stack>
                    </Stack>

                    <Box sx={{ padding: 1.2, paddingTop: 0 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                          <Box
                            sx={{
                              background: '#fff',
                              borderRadius: '0.8rem',
                              padding: 1.6,
                            }}
                          >
                            <Typography
                              fontSize='1.3rem'
                              fontWeight={600}
                              color='primary.main'
                            >
                              {t('settings.on_term')}:
                            </Typography>
                            <Typography
                              fontSize='1.4rem'
                              fontWeight={500}
                              color='#0B1370'
                            >
                              {t('settings.days', {
                                days: condition.termInDays,
                              })}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Box
                            sx={{
                              background: '#fff',
                              borderRadius: '0.8rem',
                              padding: 1.6,
                            }}
                          >
                            <Typography
                              fontSize='1.3rem'
                              fontWeight={600}
                              color='primary.main'
                            >
                              {t('settings.percentage')}:
                            </Typography>
                            <Typography
                              fontSize='1.4rem'
                              fontWeight={500}
                              color='#0B1370'
                            >
                              {condition.percentage} %
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Box
                            sx={{
                              background: '#fff',
                              borderRadius: '0.8rem',
                              padding: 1.6,
                            }}
                          >
                            <Typography
                              fontSize='1.3rem'
                              fontWeight={600}
                              color='primary.main'
                            >
                              {t('settings.factoring_type')}:
                            </Typography>
                            <Typography
                              fontSize='1.4rem'
                              fontWeight={500}
                              color='#0B1370'
                            >
                              {getFactoringTypeText(
                                condition.isForDisclosedFactoring,
                                condition.isForUndisclosedFactoring,
                                condition.isForReverseFactoring,
                                condition.isForExportFactoring,
                                condition.isForImportFactoring,
                                t
                              )}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  </Box>
                </Stack>
              ))
          )}
        </Stack>
      </Stack>
      <FactoringConditionsModal
        ref={factoringConditionsModalRef}
        onSuccess={handleModalSuccess}
        editConditionId={editingConditionId}
      />
      <ConfirmDeletion
        onSubmit={confirmDelete}
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        type='agreement'
      />
    </ComponentWithLoader>
  );
};

export default ConditionsComponent;
